import { useCallback } from "react";
import { commitLocalUpdate, commitMutation, graphql } from "react-relay";

import type { Event } from "~/calendar/CalendarPage";
import { confirm } from "~/lib/prompts";
import { environment } from "~/models";

import type { useEventVisibilityToggleMutation as MutationType } from "~/__generated__/useEventVisibilityToggleMutation.graphql";

const mutation = graphql`
	mutation useEventVisibilityToggleMutation($guid: String!) {
		hideEvent(guid: $guid) {
			ok
		}
	}
`;

export function invalidateHiddenEvent(id: string) {
	commitLocalUpdate(environment, store => {
		const event = store.get(id);
		if (event) {
			event.invalidateRecord();
		}
	});
}

export default function useEventVisibilityToggle(
	event: Pick<Event, "guid" | "isHidden">,
	messages: {
		onHide: React.ReactNode;
		onReveal: React.ReactNode;
	},
) {
	return useCallback(() => {
		confirm(event.isHidden ? messages.onReveal : messages.onHide, {
			yesText: event.isHidden ? "Show" : "Hide",
			title: event.isHidden ? "Show selected event?" : "Hide selected event?",
		}).then(result => {
			if (!result) {
				return;
			}

			commitMutation<MutationType>(environment, {
				mutation,
				variables: { guid: event.guid },
				onCompleted: ({ hideEvent }) => {
					if (hideEvent?.ok) {
						invalidateHiddenEvent(event.guid);
					}
				},
			});
		});
	}, [event, messages]);
}
